/**
 * Retainers — a snapshot of the catalog with allowances that reset every period.
 *
 * `SERVICES.md` §Retainers: "Starting a retainer snapshots the blocks, allowances and
 * computed amounts onto it. Editing a template or the rate card does not change
 * existing clients." Everything here is the **wire** shape of that snapshot:
 * snake_case, codes and refs rather than ids, money as `{ amount, currency }` in
 * minor units.
 *
 * Two things are worth reading before the rest.
 *
 *   **A retainer item carries two kinds of amount, and they are in different
 *   currencies on purpose.** `block_amount` is a step in the build-up to the monthly
 *   figure and is therefore USD at Tier 1, exactly as a project quote's lines are
 *   (`work_order_line`, session 11) — the discount, the tier, the conversion and the
 *   rounding all come after it and apply to the total. `extra_units[].unit_amount` is
 *   a **price that will be charged**, so it is at the client's own tier and currency
 *   and already rounded to $5. `client/billing.html` §1 prints both columns side by
 *   side and they mean different things.
 *
 *   **An allowance is not stored.** `allowance_status` is computed from
 *   `deliverable` rows every time it is asked for (`.docs/12-retainers.md`: "computed
 *   from deliverables, never stored"), because a stored counter and a list of rows
 *   are two answers to one question and only one of them can be re-derived.
 */
import { z } from 'zod';
import { CONTENT_TYPE_CODES } from '../pricing.js';
import { contentPlanMachine, retainerChangeMachine, retainerMachine } from '../states.js';
import { CatalogCodeSchema, CustomLineSchema, MonthlyBreakdownSchema, PackageItemSchema, } from './catalog.js';
import { CurrencySchema, DateOnlySchema, MoneySchema, PeriodSchema, Rfc3339Schema, } from './primitives.js';
import { ClientSlugSchema, ContentPlanRefSchema, RetainerChangeRefSchema } from './refs.js';
/* ---------------------------------------------------------------- the words */
/**
 * What happens when work goes past an allowance.
 *
 * `.docs/00-index.md` §6: "`ask` refuses past the allowance without an approved extra
 * ref. `rolling` is set once by an approver and audited; it is the standing approval
 * `deliverables_record` needs. Default `ask`."
 */
export const OVERAGE_POLICIES = ['ask', 'rolling'];
export const OveragePolicySchema = z
    .enum(OVERAGE_POLICIES)
    .describe('ask — work past this allowance is refused unless an approved extra already covers ' +
    'it, which is the default. rolling — a standing approval an owner or admin gave ' +
    'once, so each period’s overage becomes an approved extra as it is delivered.');
/**
 * What a retainer item is a snapshot of.
 *
 * `catalog` — a block of a rate-card service, priced by the engine with its
 * allowance and its overage prices. `custom` — a fixed line one client agreed
 * (`PRICING.md` §Custom lines): a name, a scope and an amount, nothing metered and
 * nothing computed. The two are one table because they are one list on the invoice
 * and one list on `client/billing.html` §1; the kind is what says which columns mean
 * anything.
 */
export const RETAINER_ITEM_KINDS = ['catalog', 'custom'];
export const RetainerItemKindSchema = z
    .enum(RETAINER_ITEM_KINDS)
    .describe('catalog — a block of a rate-card service, with its allowance and overage prices. ' +
    'custom — a fixed line agreed with this client outside the card; service is null, ' +
    'nothing is metered, and the amount is in the billing currency exactly as agreed.');
export const RetainerStatusSchema = z
    .enum(retainerMachine.states)
    .describe('active — billing normally. past_due — a charge failed and it is being retried; ' +
    'still billing. paused — no retainer line and no allowances. ending — cancellation ' +
    'is scheduled and it stops at period end. cancelled — over.');
export const RetainerChangeStatusSchema = z
    .enum(retainerChangeMachine.states)
    .describe('proposed — waiting on the client. approved — it takes effect on its billing day. ' +
    'declined — they said no. applied — the billing run has written it onto the retainer.');
export const ContentPlanStatusSchema = z
    .enum(contentPlanMachine.states)
    .describe('draft — the studio is still writing it. proposed — waiting for the client’s sign-off. ' +
    'signed_off — released into production. changes_requested — they want it changed.');
/** The four states one piece of a content plan moves through as the month runs. */
export const CONTENT_PLAN_ITEM_STATES = [
    'planned',
    'scheduled',
    'published',
    'needs_signoff',
];
export const ContentPlanItemStateSchema = z
    .enum(CONTENT_PLAN_ITEM_STATES)
    .describe('planned — on the calendar. scheduled — queued with the platform. published — it went ' +
    'out, and the deliverable is linked. needs_signoff — it is waiting on the client, ' +
    'which is what the three "needs you" days on report-2026-08.html’s calendar are.');
export const ContentTypeCodeSchema = z
    .enum(CONTENT_TYPE_CODES)
    .describe('reel, carousel, static or story (PRICING.md, Content types).');
/* ----------------------------------------------------------------- the inputs */
/**
 * One widget allowance a block carries.
 *
 * A **list of pairs** rather than an object keyed by code, because a tool input has
 * to be a `z.strictObject` all the way down (`actions/define.ts`): a free-form map
 * publishes `additionalProperties` and a misspelt widget code would be silently
 * dropped rather than refused by name. The service turns it into a map on the way in.
 */
export const IncludedWidgetQtySchema = z
    .strictObject({
    widget_type: CatalogCodeSchema.describe('The widget type, by code — "chatbot", "booking".'),
    qty: z
        .int()
        .min(0)
        .max(1_000_000)
        .describe('How many of its units the block includes every month before anything bills.'),
})
    .describe('One widget allowance inside a retainer block.');
/**
 * One block of a package, as a caller names it.
 *
 * `PackageItemSchema` (the catalog's, which is what the pricing engine takes) plus
 * the one thing a retainer knows that a price does not: how much of a widget's usage
 * this block includes every month (`SERVICES.md` §Postpay). Nothing on the rate card
 * says which package includes widget allowances, because it is a per-client
 * negotiation — `client/billing.html` §5: "the unit price is agreed when it goes
 * live".
 */
export const RetainerItemInputSchema = PackageItemSchema.extend({
    included_widget_qty: z
        .array(IncludedWidgetQtySchema)
        .max(20)
        .default([])
        .describe('Widget units this block includes every month. Left out, none: every event is billed.'),
}).describe('One block of a retainer: a monthly service, how many blocks, and what it includes.');
export const StartRetainerSchema = z
    .strictObject({
    name: z
        .string()
        .min(2)
        .max(80)
        .describe('What the client calls their package — "Growth+". It is on every invoice.'),
    package: CatalogCodeSchema.optional().describe('A named preset to build it from — "growth", "scale". Give this or items, not both. ' +
        'catalog_list_packages has them.'),
    items: z
        .array(RetainerItemInputSchema)
        .max(30)
        .default([])
        .describe('The blocks themselves, when it is not one of the presets. The minimum retainer is ' +
        'one block or one custom line (PRICING.md).'),
    custom_lines: z
        .array(CustomLineSchema)
        .max(10)
        .default([])
        .describe('Fixed lines outside the rate card — work agreed with this client at a price the ' +
        'card does not sell as a block (PRICING.md, Custom lines). They ride with a preset ' +
        'or with listed blocks, or stand alone. Left out, none.'),
    started_on: DateOnlySchema.optional().describe('The signup date. Left out, today. The first period is the first **full** month after ' +
        'it, unless it is the 1st.'),
})
    .describe('A retainer to start: the package, any custom lines, and the day it starts.');
export const ProposeRetainerChangeSchema = z
    .strictObject({
    items: z
        .array(RetainerItemInputSchema)
        .max(30)
        .default([])
        .describe('The whole package after the change, not the difference — the discount and the ' +
        'rounding apply to the new total rather than to each change on its own. Empty ' +
        'only when custom_lines carries the whole package.'),
    custom_lines: z
        .array(CustomLineSchema)
        .max(10)
        .default([])
        .describe('The custom lines after the change — the whole set, not the difference. A line the ' +
        'retainer has today and this leaves out is dropped by the change; a line with a ' +
        'new amount is re-priced by it.'),
    effective_on: DateOnlySchema.describe('The billing day it takes effect: the 1st of a month, and not one that has passed. ' +
        'Mid-period work is an extra (SERVICES.md).'),
    note: z
        .string()
        .max(4_000)
        .default('')
        .describe('What to tell the client about it — the break-even prose on client/requests.html. ' +
        'Yours to write; nothing computes it.'),
})
    .describe('A change to a package, priced both ways and sent for the client to decide.');
export const SetOveragePolicySchema = z
    .strictObject({
    service: CatalogCodeSchema.describe('Which block, by rate-card code.'),
    policy: OveragePolicySchema,
    approver_email: z
        .string()
        .max(320)
        .optional()
        .describe('Who on the client’s account gave the standing approval. Required when the studio is ' +
        'recording one, refused when the client is giving it themselves — the studio does ' +
        'not approve money on a client’s behalf, it records that they did.'),
    consent_file_id: z
        .string()
        .optional()
        .describe('Their written consent, by file id. Required with approver_email and refused without ' +
        'it, exactly as work_orders_record_offline_approval requires one.'),
})
    .describe('The standing decision about work past one allowance.');
/* ------------------------------------------------------------------- the rows */
export const RetainerItemSchema = z
    .object({
    kind: RetainerItemKindSchema,
    service: CatalogCodeSchema.nullable().describe('The rate-card service, by code, as it was at signup. Null for a custom line, which ' +
        'is not on the card.'),
    service_name: z
        .string()
        .describe('What it was called then — "Social content — Base". For a custom line, its name as ' +
        'it reads on the invoice.'),
    block_scope: z
        .string()
        .describe('What one block covered, in the rate card’s own words. For a custom line, the ' +
        'scope as it was agreed.'),
    blocks: z.int().describe('How many blocks of it. Always 1 for a custom line.'),
    allowance_unit: z
        .string()
        .nullable()
        .describe('What the allowance counts — "article", "credit", "hour" — or null when it is ' +
        'not metered. SEO / GEO is the one that is not.'),
    allowance_qty: z
        .int()
        .nullable()
        .describe('How many of them the item includes every period, already × blocks.'),
    block_amount: MoneySchema.describe('USD at Tier 1, per block per month, as the card had it at signup. This is a step in ' +
        'the build-up, not what the client pays — the discount, the tier and the rounding ' +
        'come after it. For a custom line it is the agreed amount in the billing currency, ' +
        'which is exactly what is charged; the currency on it says which.'),
    amount: MoneySchema.describe('block_amount × blocks, USD at Tier 1 — the item’s list line. For a custom line, the ' +
        'agreed amount in the billing currency.'),
    extra_units: z
        .array(z
        .object({
        code: z.string().describe('The unit, by code — "article", "reel", "hour".'),
        noun: z.string().describe('One of them — "article".'),
        noun_plural: z.string().describe('More than one — "articles".'),
        unit_amount: MoneySchema.describe('One unit at this client’s tier, in their currency, rounded to $5. What an ' +
            'overage extra is billed at.'),
    })
        .describe('One thing that can be bought past this item’s allowance.'))
        .describe('The overage prices, snapshotted. A later rate-card change does not touch them.'),
    overage_policy: OveragePolicySchema,
    rolling_set_by_name: z
        .string()
        .nullable()
        .describe('Who gave the standing approval, by name. Null while the policy is ask.'),
    rolling_set_at: Rfc3339Schema.nullable().describe('When they gave it. Null while it is ask.'),
    included_widget_qty: z
        .array(IncludedWidgetQtySchema)
        .describe('Widget units this block includes every month — SERVICES.md §Postpay: "A package may ' +
        'include an allowance per widget; only the excess is billed." Empty when it ' +
        'includes none.'),
})
    .describe('One block of a retainer, as it was priced and scoped on the day it started.');
export const RetainerSchema = z
    .object({
    client: ClientSlugSchema,
    name: z.string().describe('What the package is called — "Growth+".'),
    package: CatalogCodeSchema.nullable().describe('The named preset it was built from, by code, or null for one built for this client.'),
    status: RetainerStatusSchema,
    started_on: DateOnlySchema.describe('The signup date. Allowances start with the first period.'),
    ends_on: DateOnlySchema.nullable().describe('The last day it covers, once cancellation is ' + 'scheduled. Null while it is open-ended.'),
    paused_at: Rfc3339Schema.nullable().describe('When work was paused, or null.'),
    past_due_since: Rfc3339Schema.nullable().describe('When the first charge failed, or null.'),
    cancellation_scheduled_at: Rfc3339Schema.nullable().describe('When somebody scheduled the cancellation, or null.'),
    market_tier: z.int().describe('The market tier it was priced at: 1, 2 or 3.'),
    tier_multiplier_bps: z
        .int()
        .describe('That tier as basis points — 10000, 7000 or 5000 — snapshotted at signup.'),
    bundle_discount_bps: z
        .int()
        .describe('The bundle band it reached: 0, 500, 1000 or 1500 basis points.'),
    monthly_amount: MoneySchema.describe('What is charged every month, in the billing currency.'),
    currency: CurrencySchema,
    billed_through: PeriodSchema.nullable().describe('The last period the billing run has invoiced. Null before the first invoice; a ' +
        'deliverable cannot be recorded into a period at or before it without late: true.'),
    items: z.array(RetainerItemSchema).describe('The blocks, in the order they were priced.'),
    pricing_breakdown: MonthlyBreakdownSchema.nullable().describe('The whole build-up behind monthly_amount: list, bundle discount, tier, conversion and ' +
        'the rounding line. What client/billing.html §1 prints under the table.'),
    created_at: Rfc3339Schema,
    updated_at: Rfc3339Schema,
})
    .describe('A client’s retainer: the snapshot, the terms and what it costs every month.');
/* --------------------------------------------------------------- allowances */
export const AllowanceStatusSchema = z
    .object({
    service: CatalogCodeSchema.describe('The block, by rate-card code.'),
    service_name: z.string().describe('What it is called.'),
    unit: z.string().describe('What the allowance counts — "article", "credit", "hour".'),
    included: z.int().describe('How many the period includes.'),
    used: z.number().describe('How many have been delivered. Fractional for hours.'),
    over: z.number().describe('How many are past the allowance. Zero, never negative.'),
    credits: z
        .object({
        reel: z.int().describe('How many reels were published this period.'),
        carousel: z.int().describe('How many carousels.'),
        static: z.int().describe('How many static posts.'),
        story: z.int().describe('How many stories.'),
    })
        .nullable()
        .describe('For a content block, the pieces behind the credits, by type — report-2026-08.html’s ' +
        '"Where the 106 credits went". Null for every other block.'),
    extra_unit: z
        .string()
        .nullable()
        .describe('The unit an overage is priced from, by code, or null when there is none.'),
    extra_unit_amount: MoneySchema.nullable().describe('What one of them costs at this client’s tier and currency, snapshotted at signup.'),
})
    .describe('One allowance for one period: what it includes, what was used, what is over.');
export const AllowancesSchema = z
    .object({
    period: PeriodSchema.describe('The month these are counted over.'),
    allowances: z.array(AllowanceStatusSchema).describe('One per metered block, in package order.'),
})
    .describe('Every allowance on a retainer for one period. Computed, never stored.');
/* ------------------------------------------------------------- deliverables */
export const DeliverableSchema = z
    .object({
    id: z.string().describe('The deliverable’s id. What a content-plan item links to.'),
    client: ClientSlugSchema,
    period: PeriodSchema.describe('The period whose allowance it draws on, YYYY-MM.'),
    service: CatalogCodeSchema.describe('The block it was delivered against, by code.'),
    content_type: ContentTypeCodeSchema.nullable().describe('For social content, which kind of piece. Null for every other service.'),
    qty: z.number().describe('How many — 5 articles, 2 reels, 1.5 hours.'),
    credits: z
        .int()
        .nullable()
        .describe('What it cost against the content allowance. Null when the block is not metered ' +
        'in credits.'),
    title: z.string().nullable().describe('What it was, in a line — "Hammam towels for hotels".'),
    refs: z
        .array(z.string())
        .describe('Free identifiers from the report — "V-2609", "ODO-243". Ours, not this system’s.'),
    channels: z
        .array(z.string())
        .describe('Where a piece ran — "instagram", "tiktok". A piece on two channels costs one credit, ' +
        'not two (report-2026-08.html).'),
    delivered_on: DateOnlySchema.describe('The day it landed.'),
    file_ids: z.array(z.string()).describe('The files that are the deliverable, by id.'),
    extra_ref: z
        .string()
        .nullable()
        .describe('The approved extra covering the part of it that was past the allowance, REQ-NNN.'),
    over: z.number().describe('How much of this deliverable was past the allowance when recorded.'),
    created_at: Rfc3339Schema,
})
    .describe('One thing delivered against a retainer allowance.');
export const RecordDeliverableSchema = z
    .strictObject({
    service: CatalogCodeSchema.describe('The block it is delivered against, by rate-card code — "seo_content", ' +
        '"social_content_base", "odoo_management_base". It has to be a block the retainer has.'),
    content_type: ContentTypeCodeSchema.optional().describe('Which kind of piece, for a social content block. Required there and refused elsewhere, ' +
        'because the credits and the overage price are both per type.'),
    qty: z
        .number()
        .gt(0)
        .max(10_000)
        .refine((value) => Number(value.toFixed(3)) === value, 'A quantity has at most three decimal places.')
        .describe('How many — 1 article, 3 reels, 1.5 hours. More than nothing, and at most three ' +
        'decimal places, which is what the column holds.'),
    title: z.string().max(300).optional().describe('What it was, in a line. For the report.'),
    refs: z
        .array(z.string().min(1).max(60))
        .max(50)
        .default([])
        .describe('Your own identifiers for it — "V-2609", "ODO-243". They are printed, not parsed.'),
    channels: z
        .array(z.string().min(1).max(40))
        .max(20)
        .default([])
        .describe('Where a piece ran. Two channels is still one credit.'),
    delivered_on: DateOnlySchema.optional().describe('The day it landed. Left out, today in the studio’s zone. Its month is the period it ' +
        'draws its allowance from.'),
    file_ids: z
        .array(z.string())
        .max(50)
        .default([])
        .describe('Files that are the deliverable, by id. They have to be this client’s.'),
    extra_work_order_ref: z
        .string()
        .optional()
        .describe('An approved extra that already covers the part of this past the allowance, REQ-NNN. ' +
        'Give it when the allowance is full and the client has agreed to the overage.'),
    late: z
        .boolean()
        .default(false)
        .describe('Record it into the **current** period, with a note, when the period it belongs to ' +
        'has already been billed. Staff only. It never reopens a billed period.'),
})
    .describe('Work delivered against a retainer allowance.');
export const RecordedDeliverableSchema = z
    .object({
    deliverable: DeliverableSchema,
    allowance: AllowanceStatusSchema.nullable().describe('That block’s allowance after this was recorded, or null when the block is not metered ' +
        '— SEO / GEO is sold as one block and counts nothing.'),
    extra_ref: z
        .string()
        .nullable()
        .describe('The extra the overage was billed to, if any — an existing one or a new rolling ' + 'one.'),
    late: z
        .boolean()
        .describe('True when it was recorded into the current period because its own was billed.'),
})
    .describe('A recorded deliverable, and where the allowance stands after it.');
/* ---------------------------------------------------------- package changes */
export const RetainerChangeSchema = z
    .object({
    ref: RetainerChangeRefSchema,
    client: ClientSlugSchema,
    status: RetainerChangeStatusSchema,
    proposed_items: z
        .array(PackageItemSchema)
        .describe('The whole package after the change, not the difference.'),
    proposed_custom_lines: z
        .array(CustomLineSchema)
        .describe('The custom lines after the change — the whole set. Empty when there are none.'),
    before: MonthlyBreakdownSchema.describe('What the package costs today, step by step.'),
    after: MonthlyBreakdownSchema.describe('What it would cost, step by step.'),
    delta_amount: MoneySchema.describe('after − before. Positive is more a month.'),
    effective_on: DateOnlySchema.describe('The billing day it takes effect — always the 1st.'),
    note: z
        .string()
        .describe('What the studio wrote about it — the break-even prose on client/requests.html. Staff ' +
        'text, never computed.'),
    proposed_at: Rfc3339Schema,
    approved_by_name: z.string().nullable().describe('Who approved it, by name, or null.'),
    approved_at: Rfc3339Schema.nullable(),
    declined_at: Rfc3339Schema.nullable(),
    applied_at: Rfc3339Schema.nullable().describe('When the billing run wrote it onto the retainer.'),
})
    .describe('A proposed change to a package. It never takes effect mid-period.');
/* ------------------------------------------------------------ content plans */
export const ContentPlanItemSchema = z
    .object({
    id: z.string().describe('The item’s id, for update_item.'),
    on: DateOnlySchema.describe('The day it is planned for.'),
    content_type: ContentTypeCodeSchema,
    channels: z.array(z.string()).describe('Where it runs — "instagram", "tiktok", "pinterest".'),
    title: z.string().describe('What it is — "Mill to bathroom in 6 steps".'),
    state: ContentPlanItemStateSchema,
    credits: z.int().describe('What this piece costs against the allowance, by its type.'),
    deliverable_id: z
        .string()
        .nullable()
        .describe('The deliverable it became once it published, or null.'),
})
    .describe('One piece on a content calendar.');
export const ContentPlanSchema = z
    .object({
    ref: ContentPlanRefSchema,
    client: ClientSlugSchema,
    period: PeriodSchema.describe('The month it covers, YYYY-MM.'),
    status: ContentPlanStatusSchema,
    credits_planned: z.int().describe('What the whole plan costs against the content allowance.'),
    credits_included: z.int().describe('What the retainer’s content block includes that period.'),
    over: z.int().describe('credits_planned − credits_included, or zero.'),
    signed_off_by_name: z.string().nullable().describe('Who signed it off, by name, or null.'),
    signed_off_at: Rfc3339Schema.nullable(),
    late_signoff: z
        .boolean()
        .describe('True when it was signed off after the first item’s own day had already passed, so the ' +
        'calendar shifts. A warning, not a refusal.'),
    changes_note: z.string().nullable().describe('What the client asked to be changed, or null.'),
    file_id: z.string().nullable().describe('The calendar as a PDF, by file id, or null.'),
    items: z.array(ContentPlanItemSchema).describe('The pieces, in calendar order.'),
    created_at: Rfc3339Schema,
    updated_at: Rfc3339Schema,
})
    .describe('A month of planned content, and where its sign-off stands. No money is attached.');
export const ContentPlanItemInputSchema = z
    .strictObject({
    on: DateOnlySchema.describe('The day it is planned for. It has to fall inside the period.'),
    content_type: ContentTypeCodeSchema,
    channels: z
        .array(z.string().min(1).max(40))
        .max(20)
        .default([])
        .describe('Where it runs. Two channels is one piece and one credit, not two.'),
    title: z.string().min(1).max(300).describe('What it is, in a line.'),
})
    .describe('One piece to put on the calendar.');
export const DraftContentPlanSchema = z
    .strictObject({
    period: PeriodSchema.describe('The month it covers, YYYY-MM. It is also the reference.'),
    items: z
        .array(ContentPlanItemInputSchema)
        .max(200)
        .default([])
        .describe('The pieces, in any order. They are stored by day.'),
})
    .describe('A month of planned content. Drafting again rewrites the same plan.');
export const UpdateContentPlanItemSchema = z
    .strictObject({
    item_id: z.string().describe('Which piece, by the id content_plans_get answers with.'),
    state: ContentPlanItemStateSchema,
    deliverable_id: z
        .string()
        .optional()
        .describe('The deliverable it became, for state "published". Required there and cleared ' +
        'otherwise: a published piece and a deliverable are one fact.'),
})
    .describe('One piece of a content plan, moved along as the month runs.');
/* ------------------------------------------------------- what session 14 bills */
/**
 * The retainer's own line on a monthly invoice, as `billing.run` step 3 takes it.
 *
 * `.docs/14-billing-invoices.md`: "`retainers.retainerLineFor(retainer, period)`
 * unless `ending` past its last period or `paused` → retainer line (in advance)."
 * Deliberately the shape of an `invoice_line` and not of anything here: session 14
 * owns that table, and this is the draft it copies.
 */
export const RetainerLineDraftSchema = z
    .object({
    kind: z.literal('retainer').describe('Which kind of invoice line this is.'),
    description: z
        .string()
        .describe('As it reads on the invoice — "Retainer — Growth+, September 2026".'),
    period: PeriodSchema.describe('The month it covers, which is billed in advance.'),
    qty: z.number().describe('One. A retainer is a month of a package, not a quantity of things.'),
    unit_amount: MoneySchema.describe('The monthly amount.'),
    amount: MoneySchema.describe('qty × unit_amount — the same figure.'),
})
    .describe('The retainer line of a monthly invoice, before session 14 writes it.');
//# sourceMappingURL=retainers.js.map